/* eslint-disable no-unused-vars, no-dupe-else-if, react/no-unescaped-entities, react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  SafText,
  SafTable,
  SafButton,
  SafIcon,
  SafCombobox,
  SafOption,
  SafPagination,
  SafDialog,
  SafAnchor,
  SafCheckbox,
  SafAlert,
} from "@saffron/core-components/react";
import "../../App.css";
import { callMiddleware } from "../../util/api"; // Import callMiddleware
import { ENVIRONMENTS, matchesTechStackProvider, isMajorCloudProviderValue } from "../../util/constants";
import {
  Justification as JustificationComponent,
  TechnologyStackComparisonPage as TechnologyStackComparison,
} from "../../features/tech-stack";

const DevopsTab = ({ selectedCloudProvider = "" }) => {
  const [runtimeData, setRuntimeData] = useState([]);
  const [techDetailsMap, setTechDetailsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controlled, setControlled] = useState({
    totalItemCount: 0,
    currentPageIndex: 1,
    itemsArray: [5, 10, 25, 50, 100, 250, 500],
    itemsPerPage: 25,
  });
  const [filterValue, setFilterValue] = useState("Proposed");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deviations, setDeviations] = useState([]);

  // Pattern selection and removal states
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [alertMessage, setAlertMessage] = useState("");

  const { assetId: urlAssetId } = useParams();

  const Data = [
    { title: "Proposed Score", percentage: <em>coming soon...</em> },
    { title: "Non-Production Score", percentage: <em>coming soon...</em> },
    { title: "Production Score", percentage: <em>coming soon...</em> },
    { title: "Issues", percentage: <em>coming soon...</em> },
  ];

  const [showAll, setShowAll] = useState(false);
  const visiblecards = showAll ? Data : Data.slice(0, 4);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedTab, setSelectedTab] = useState("DevOps");

  // Handler for pattern checkbox changes
  // Handler for pattern checkbox changes
  const handlePatternCheckboxChange = (serviceId, patternRefId, isChecked) => {
    const key = `${serviceId}-${patternRefId}`;
    setSelectedPatterns((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
  };

  // Handler for remove patterns button click
  const handleGlobalRemovePatternsClick = () => {
    if (selectedPatterns.size === 0) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("Please select at least one pattern to remove.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
      return;
    }
    setRemoveDialogOpen(true);
  };

  // Confirm pattern removal
  const confirmPatternRemoval = async () => {
    setRemoveDialogOpen(false);

    try {
      if (selectedPatterns.size === 0) return;

      // Group selected pattern_ref_ids by service ID
      const patternsByService = {};
      Array.from(selectedPatterns).forEach((key) => {
        const parts = key.split("-");
        const serviceId = parts[0];
        const patternRefId = parts.slice(1).join("-"); // Handle IDs with dashes

        if (!patternsByService[serviceId]) {
          patternsByService[serviceId] = [];
        }
        patternsByService[serviceId].push(patternRefId);
      });

      // Fetch current data
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });
      let currentTechStacks, currentDeviations;

      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      // Update the data by removing selected pattern_ref_ids from all services
      const updatedTechStacks = currentTechStacks.map((item) => {
        if (
          (selectedTab === "Frontend" && item.tech_stack === "frontend") ||
          (selectedTab === "Backend" && item.tech_stack === "backend") ||
          (selectedTab === "DevOps" && item.tech_stack === "devops")
        ) {
          const updatedEnvironments = item.tech_stack_environment.map((env) => {
            if (env.services && Array.isArray(env.services)) {
              return {
                ...env,
                services: env.services.map((service) => {
                  // Check if this service has patterns to remove
                  if (patternsByService[service.service_id]) {
                    // CHANGED: Filter pattern_ref_id instead of pattern_names
                    const updatedPatternRefIds = (
                      service.pattern_ref_id || []
                    ).filter((refId) => {
                      const shouldKeep =
                        !patternsByService[service.service_id].includes(refId);
                      return shouldKeep;
                    });

                    return {
                      ...service,
                      pattern_ref_id: updatedPatternRefIds, // CHANGED: Update pattern_ref_id
                    };
                  }
                  return service;
                }),
              };
            }
            return env;
          });

          return {
            ...item,
            tech_stack_environment: updatedEnvironments,
          };
        }
        return item;
      });

      // Update the database
      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: updatedTechStacks,
        deviations: currentDeviations,
      };

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      // Update local state
      setRuntimeData(updatedTechStacks);

      // Clear all selected patterns
      setSelectedPatterns(new Set());

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Selected patterns removed successfully!");

      // Auto-hide success message
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    } catch (err) {
      console.error("Error removing patterns:", err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to remove patterns. Please try again.");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    }
  };

  const handleOnClose = () => {
    setOpenAlertOptions(undefined);
  };

  const handleTabChange = (event) => {
    setSelectedTab(event.detail.id);
  };

  // Fetch tech stack mapping data using callMiddleware
  const fetchTechStackDetails = async () => {
    try {
      const dbName = process.env.REACT_APP_DB_SOURCE;
      const containerId = "tech-stack-mapping";
      const query = "SELECT * FROM c";

      const resources = await callMiddleware("get", {
        dbName,
        containerName: containerId,
        query,
      });

      // Create ONE global pattern lookup for all services
      const globalPatternLookup = {};
      const techDetailsMap = {};

      if (Array.isArray(resources)) {
        resources.forEach((item) => {
          if (item.capabilities && Array.isArray(item.capabilities)) {
            item.capabilities.forEach((capability) => {
              if (capability.services && Array.isArray(capability.services)) {
                capability.services.forEach((service) => {
                  // Build the global pattern lookup using pattern_ref_id as key
                  if (
                    service.supportingPattern &&
                    Array.isArray(service.supportingPattern)
                  ) {
                    service.supportingPattern.forEach((pattern) => {
                      // CHANGED: Use pattern_ref_id instead of pattern_name as the key
                      globalPatternLookup[pattern.pattern_ref_id] = pattern;
                    });
                  }

                  // Store service details without individual pattern lookup
                  techDetailsMap[service.tech_radar_id] = {
                    categoryName: item.category || "",
                    technologyName: service.technology_name || "",
                    capability: capability.capability || "",
                    recommendation: service.recommendation || "",
                    provider: service.provider || "",
                    description: service.description || "",
                  };
                });
              }
            });
          }
        });
      }

      // Store both the tech details and the global pattern lookup
      setTechDetailsMap({
        ...techDetailsMap,
        _globalPatternLookup: globalPatternLookup,
      });
    } catch (error) {
      console.error("Error fetching tech stack details from middleware", error);
    }
  };

  // Fetch technology stacks data using callMiddleware
  const fetchTechnologyStacksData = React.useCallback(async () => {
    if (!urlAssetId) {
      console.error("No asset ID available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const data = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      if (
        data &&
        data.technology_stacks &&
        Array.isArray(data.technology_stacks)
      ) {
        setRuntimeData(data.technology_stacks);
        setDeviations(data.deviations || []);
      } else if (
        Array.isArray(data) &&
        data.length > 0 &&
        data[0].technology_stacks
      ) {
        setRuntimeData(data[0].technology_stacks);
        setDeviations(data[0].deviations || []);
      } else {
        console.warn("Unexpected data structure:", data);
        setRuntimeData([]);
        setDeviations([]);
      }
    } catch (err) {
      console.error("Error fetching technology stacks data:", err);
      setError(
        "Failed to fetch technology stack data. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  }, [urlAssetId]);

  useEffect(() => {
    fetchTechnologyStacksData();
    fetchTechStackDetails();

    // Listen for refresh event from TechnologyStackComparison
    const handleRefresh = () => {
      fetchTechnologyStacksData();
    };
    window.addEventListener("refresh-tech-stack-table", handleRefresh);
    return () => {
      window.removeEventListener("refresh-tech-stack-table", handleRefresh);
    };
  }, [fetchTechnologyStacksData]);

  const filterByEnvironment = useMemo(() => {
    return runtimeData
      .map((item) => {
        if (!filterValue || filterValue === "All") {
          return item;
        }
        const filteredEnvironments = item.tech_stack_environment.filter(
          (env) => env.environment === filterValue
        );
        return {
          ...item,
          tech_stack_environment: filteredEnvironments,
        };
      })
      .filter(
        (item) =>
          !filterValue ||
          filterValue === "All" ||
          item.tech_stack_environment.length > 0
      );
  }, [runtimeData, filterValue]);

  const filteredData = useMemo(() => {
    return runtimeData.filter((item) => {
      if (
        !item.tech_stack_environment ||
        !Array.isArray(item.tech_stack_environment)
      ) {
        return false;
      }

      const matchesTab = item.tech_stack === "devops";
      if (!matchesTab) return false;

      if (!filterValue || filterValue === "All") {
        // Apply cloud provider filter if selected
        if (selectedCloudProvider) {
          return item.tech_stack_environment.some((env) =>
            env.services?.some((service) => {
              const techDetails = techDetailsMap[service.service_id];
              return (
                matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
                !isMajorCloudProviderValue(techDetails?.provider)
              );
            })
          );
        }
        return true;
      }

      // Filter by environment
      const hasMatchingEnvironment = item.tech_stack_environment.some(
        (env) => env.environment === filterValue
      );

      if (!hasMatchingEnvironment) return false;

      // Apply cloud provider filter if selected
      if (selectedCloudProvider) {
        const matchingEnv = item.tech_stack_environment.find(
          (env) => env.environment === filterValue
        );
        return matchingEnv?.services?.some((service) => {
          const techDetails = techDetailsMap[service.service_id];
          return (
            matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
            !isMajorCloudProviderValue(techDetails?.provider)
          );
        });
      }

      return true;
    });
  }, [runtimeData, filterValue, selectedCloudProvider, techDetailsMap]);

  const paginatedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    const allServices = [];

    // Extract the global pattern lookup once
    const globalPatternLookup = techDetailsMap._globalPatternLookup || {};

    filteredData.forEach((item) => {
      if (
        item.tech_stack_environment &&
        Array.isArray(item.tech_stack_environment)
      ) {
        const environments =
          !filterValue || filterValue === "All"
            ? item.tech_stack_environment
            : item.tech_stack_environment.filter(
                (env) => env.environment === filterValue
              );

        environments.forEach((env) => {
          // Handle new structure with services array
          if (env.services && Array.isArray(env.services)) {
            env.services.forEach((service) => {
              const techDetails = techDetailsMap[service.service_id];

              // Skip only if the service belongs to a different specific major cloud provider.
              // Provider-agnostic services (N/A, empty, non-major, or multi-cloud) are always shown.
              if (
                selectedCloudProvider &&
                !matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) &&
                isMajorCloudProviderValue(techDetails?.provider)
              ) {
                return;
              }

              if (techDetails) {
                // CHANGED: Use pattern_ref_id instead of pattern_names
                const fullPatterns = (service.pattern_ref_id || [])
                  .map((refId) => {
                    return globalPatternLookup[refId] || null;
                  })
                  .filter(Boolean);

                allServices.push({
                  ...techDetails,
                  environment: env.environment,
                  tech_radar_id: service.service_id,
                  // Use the reconstructed full pattern objects
                  supportingPattern: fullPatterns,
                });
              } else {
                // Unknown services have no specific provider (N/A), always include them
                allServices.push({
                  categoryName: "N/A",
                  technologyName:
                    service.service_name || `Service ID: ${service.service_id}`,
                  capability: "N/A",
                  recommendation: service.service_name
                    ? "Couldn't find in Tech Radar"
                    : "N/A",
                  provider: "N/A",
                  environment: env.environment,
                  tech_radar_id: service.service_id || service.service_name,
                  supportingPattern: [],
                });
              }
            });
          }
          // Handle old structure with service_ids array (backward compatibility)
          else if (env.service_ids && Array.isArray(env.service_ids)) {
            env.service_ids.forEach((serviceId) => {
              const techDetails = techDetailsMap[serviceId];
              if (techDetails) {
                allServices.push({
                  ...techDetails,
                  environment: env.environment,
                  tech_radar_id: serviceId,
                  supportingPattern: techDetails.supportingPattern || [],
                });
              } else {
                allServices.push({
                  categoryName: "Unknown",
                  technologyName: techDetails.technologyName
                    ? techDetails.technologyName
                    : techDetails.service_name
                    ? techDetails.service_name
                    : `Service ID: ${serviceId}`,
                  capability: "Unknown",
                  recommendation: "Unknown",
                  provider: "Unknown",
                  environment: env.environment,
                  tech_radar_id: serviceId,
                  supportingPattern: [],
                });
              }
            });
          }
        });
      }
    });

    const startIndex =
      (controlled.currentPageIndex - 1) * controlled.itemsPerPage;
    const endIndex = startIndex + controlled.itemsPerPage;

    // Apply sorting and pagination
    let sortedServices = [...allServices];

    if (sortField) {
      sortedServices.sort((a, b) => {
        let valueA, valueB;

        switch (sortField) {
          case "Category":
            valueA = a.categoryName || "";
            valueB = b.categoryName || "";
            break;
          case "Capability":
            valueA = a.capability || "";
            valueB = b.capability || "";
            break;
          case "TechnologyName":
            valueA = a.technologyName || "";
            valueB = b.technologyName || "";
            break;
          case "Recommendation":
            valueA = a.recommendation || "";
            valueB = b.recommendation || "";
            break;
          case "Provider":
            valueA = a.provider || "";
            valueB = b.provider || "";
            break;
          default:
            return 0;
        }

        if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
        if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    const paginatedServices = sortedServices.slice(startIndex, endIndex);
    return paginatedServices;
  }, [
    filteredData,
    controlled.currentPageIndex,
    controlled.itemsPerPage,
    techDetailsMap,
    sortField,
    sortOrder,
    filterValue,
    selectedCloudProvider,
  ]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const onPrevious = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.max(prev.currentPageIndex - 1, 1),
    }));
  };

  const onNext = () => {
    setControlled((prev) => ({
      ...prev,
      currentPageIndex: Math.min(
        prev.currentPageIndex + 1,
        Math.ceil(totalItems / controlled.itemsPerPage)
      ),
    }));
  };

  const onChange = (event) => {
    setControlled((prev) => ({ ...prev, currentPageIndex: event.detail }));
  };

  const onItemsPerPageChange = (event) => {
    setControlled((prev) => ({
      ...prev,
      itemsPerPage: event.detail,
      currentPageIndex: 1,
    }));
  };

  const totalItems = useMemo(() => {
    return filteredData.reduce((total, item) => {
      return (
        total +
        item.tech_stack_environment.reduce((envCount, env) => {
          if (env.services && Array.isArray(env.services)) {
            const count = selectedCloudProvider
              ? env.services.filter((service) => {
                  const techDetails = techDetailsMap[service.service_id];
                  return (
                    matchesTechStackProvider(techDetails?.provider, selectedCloudProvider) ||
                    !isMajorCloudProviderValue(techDetails?.provider)
                  );
                }).length
              : env.services.length;
            return envCount + count;
          } else if (env.service_ids && Array.isArray(env.service_ids)) {
            return envCount + env.service_ids.length;
          }
          return envCount;
        }, 0)
      );
    }, 0);
  }, [filteredData, controlled, selectedCloudProvider, techDetailsMap, filterValue]);

  useEffect(() => {
    setControlled((prev) => ({
      ...prev,
      totalItemCount: totalItems,
    }));
  }, [totalItems]);

  const handleDeleteClick = (serviceId, environment) => {
    setServiceToDelete({ id: serviceId, environment });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
    if (!serviceToDelete) return;

    try {
      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'technology-stacks'`;

      const currentData = await callMiddleware("get", {
        dbName,
        containerName,
        query,
        partitionKey: urlAssetId,
      });

      let currentTechStacks, currentDeviations;

      if (Array.isArray(currentData) && currentData.length > 0) {
        currentTechStacks = currentData[0].technology_stacks;
        currentDeviations = currentData[0].deviations || [];
      } else if (currentData && currentData.technology_stacks) {
        currentTechStacks = currentData.technology_stacks;
        currentDeviations = currentData.deviations || [];
      } else {
        throw new Error("Could not find technology stacks data");
      }

      const updatedTechStacks = currentTechStacks.map((item) => {
        if (item.tech_stack === "devops") {
          const updatedEnvironments = item.tech_stack_environment.map((env) => {
            if (env.environment === serviceToDelete.environment) {
              // Handle new structure
              if (env.services) {
                return {
                  ...env,
                  services: env.services.filter((service) => {
                    // Check both service_id and service_name for identification
                    const serviceIdentifier =
                      service.service_id || service.service_name;
                    return serviceIdentifier !== serviceToDelete.id;
                  }),
                };
              }
              // Handle old structure
              else if (env.service_ids) {
                return {
                  ...env,
                  service_ids: env.service_ids.filter(
                    (id) => id !== serviceToDelete.id
                  ),
                };
              }
            }
            return env;
          });

          return {
            ...item,
            tech_stack_environment: updatedEnvironments,
          };
        }
        return item;
      });

      const proposedServiceIds = new Set();

      updatedTechStacks.forEach((stack) => {
        stack.tech_stack_environment.forEach((env) => {
          if (env.environment === "Proposed") {
            if (env.services && Array.isArray(env.services)) {
              env.services.forEach((service) => {
                // Use service_id or service_name as identifier
                const serviceIdentifier =
                  service.service_id || service.service_name;
                const techDetails = techDetailsMap[service.service_id];
                if (techDetails) {
                  const recommendation = (
                    techDetails.recommendation || ""
                  ).toLowerCase();
                  if (
                    recommendation !== "adopt" &&
                    recommendation !== "mandate"
                  ) {
                    proposedServiceIds.add(serviceIdentifier);
                  }
                } else if (service.service_name) {
                  // For services without techDetails (service_name only), add them to track deviations
                  proposedServiceIds.add(service.service_name);
                }
              });
            } else if (env.service_ids && Array.isArray(env.service_ids)) {
              env.service_ids.forEach((serviceId) => {
                const techDetails = techDetailsMap[serviceId];
                if (techDetails) {
                  const recommendation = (
                    techDetails.recommendation || ""
                  ).toLowerCase();
                  if (
                    recommendation !== "adopt" &&
                    recommendation !== "mandate"
                  ) {
                    proposedServiceIds.add(serviceId);
                  }
                }
              });
            }
          }
        });
      });

      const updatedDeviations = currentDeviations.filter((deviation) => {
        const deviationIdentifier =
          deviation.service_id || deviation.service_name;
        return proposedServiceIds.has(deviationIdentifier);
      });

      const updatedDocument = {
        id: "technology-stacks",
        asset_id: urlAssetId,
        technology_stacks: updatedTechStacks,
        deviations: updatedDeviations,
      };

      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: updatedDocument,
        partitionKey: urlAssetId,
      });

      // Note: cloudquery_mapping document is not updated as it's a read-only data source

      // --- Update lucid_mcp_tech_summary: set present_in_tech_stack=false for this service ---
      try {
        const lucidQuery = `SELECT * FROM c WHERE c.asset_id = '${urlAssetId}' AND c.id = 'lucid_mcp_tech_summary'`;
        const lucidDataResult = await callMiddleware("get", {
          dbName,
          containerName,
          query: lucidQuery,
          partitionKey: urlAssetId,
        });

        let lucidData;
        if (
          lucidDataResult &&
          Array.isArray(lucidDataResult) &&
          lucidDataResult.length > 0
        ) {
          lucidData = lucidDataResult[0];
        } else if (
          lucidDataResult &&
          lucidDataResult.id === "lucid_mcp_tech_summary"
        ) {
          lucidData = lucidDataResult;
        }

        if (
          lucidData &&
          lucidData.lucid_summary?.combined_summary?.technologies
        ) {
          let updated = false;
          lucidData.lucid_summary.combined_summary.technologies.forEach(
            (tech) => {
              const techName =
                typeof tech === "string"
                  ? tech
                  : tech.technology || tech.name || "";

              // Match by tech_radar_id or service_name
              const removedTechDetails = techDetailsMap[serviceToDelete.id];
              const isMatch =
                (removedTechDetails &&
                  techName.toLowerCase() ===
                    removedTechDetails.technologyName?.toLowerCase()) ||
                (serviceToDelete.id &&
                  techName
                    .toLowerCase()
                    .includes(serviceToDelete.id.toLowerCase())) ||
                techName.toLowerCase() ===
                  String(serviceToDelete.id).toLowerCase();

              if (
                isMatch &&
                typeof tech === "object" &&
                tech.present_in_tech_stack !== false
              ) {
                tech.present_in_tech_stack = false;
                updated = true;
              }
            }
          );

          if (updated) {
            lucidData.last_modified = new Date().toISOString();
            await callMiddleware("insert", {
              dbName,
              containerName,
              jsonData: lucidData,
              partitionKey: urlAssetId,
            });
            console.log("Updated lucid_mcp_tech_summary after removal");
          }
        }
      } catch (lucidErr) {
        console.error(
          "Error updating lucid_mcp_tech_summary after removal:",
          lucidErr
        );
      }

      setRuntimeData(updatedTechStacks);
      setDeviations(updatedDeviations);

      setControlled((prev) => ({
        ...prev,
        currentPageIndex: 1,
      }));
    } catch (err) {
      console.error("Error deleting service:", err);
    } finally {
      setServiceToDelete(null);
    }
  };

  return (
    <div className="headers">
      <SafText>
        <strong>Environment</strong>
      </SafText>
      &nbsp;
      <br />
      <div>
        <div style={{ maxWidth: "100%", margin: "0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SafCombobox
              id="combobox-environment"
              name="environment-devops"
              onChange={(event) => setFilterValue(event.target.value)}
              value={filterValue}
              style={{ width: "300px" }}
              onFocus={(event) => {
                event.target.value = "";
              }}
              onBlur={(event) => {
                if (!event.target.value) {
                  setFilterValue("Proposed");
                }
              }}
            >
              {ENVIRONMENTS.map((env, index) => (
                <SafOption key={index}>{env}</SafOption>
              ))}
            </SafCombobox>
            <div>
              <SafButton
                appearance="secondary"
                size="medium"
                onClick={() => handleGlobalRemovePatternsClick()}
                disabled={selectedPatterns.size === 0}
                style={{ marginRight: "10px" }}
              >
                <SafIcon
                  slot="start"
                  icon-name="minus"
                  appearance="solid"
                  presentation
                />
                Remove Patterns ({selectedPatterns.size})
              </SafButton>
              {selectedCloudProvider ? (
                <Link
                  to={`/${urlAssetId}/technology-stacks/adddevopstechstack`}
                  state={{
                    cloudProvider:
                      sessionStorage.getItem(
                        `selectedCloudProvider_${urlAssetId}`
                      ) || "",
                  }}
                >
                  <SafButton
                    appearance="secondary"
                    size="small"
                    id="button-dialog-trigger"
                    aria-haspopup="dialog"
                    aria-controls="button-dialog-overlay"
                  >
                    <SafIcon
                      slot="start"
                      icon-name="plus"
                      appearance="solid"
                      presentation
                    ></SafIcon>
                    Add
                  </SafButton>
                </Link>
              ) : (
                <SafButton
                  appearance="secondary"
                  size="small"
                  id="button-dialog-trigger"
                  aria-haspopup="dialog"
                  aria-controls="button-dialog-overlay"
                  disabled={true}
                  title="Please select a cloud provider"
                >
                  <SafIcon
                    slot="start"
                    icon-name="plus"
                    appearance="solid"
                    presentation
                  ></SafIcon>
                  Add
                </SafButton>
              )}
            </div>
          </div>
          <br />
          <SafText>
            Table shows the tech Stack for the selected asset and environment
          </SafText>
          <br />
          <div style={{ width: "100%" }}>
            <SafTable
              alternating-rows="true"
              density="inherit"
              header-background="default"
              inline-borders="true"
            >
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort("TechnologyName")}
                    style={{ width: "200px" }}
                  >
                    Technology Name
                    <SafIcon
                      iconName={
                        sortField === "TechnologyName"
                          ? sortOrder === "asc"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleSort("Category")}
                    style={{ width: "200px" }}
                  >
                    Category
                    <SafIcon
                      iconName={
                        sortField === "Category"
                          ? sortOrder === "asc"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleSort("Capability")}
                    style={{ width: "200px" }}
                  >
                    Capability
                    <SafIcon
                      iconName={
                        sortField === "Capability"
                          ? sortOrder === "asc"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th style={{ width: "200px" }}>Supporting Patterns</th>
                  <th
                    onClick={() => handleSort("Recommendation")}
                    style={{ width: "200px" }}
                  >
                    Recommendation
                    <SafIcon
                      iconName={
                        sortField === "Recommendation"
                          ? sortOrder === "asc"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th style={{ width: "200px" }}>Compliance Status</th>
                  <th
                    onClick={() => handleSort("Provider")}
                    style={{ width: "150px" }}
                  >
                    Provider
                    <SafIcon
                      iconName={
                        sortField === "Provider"
                          ? sortOrder === "asc"
                            ? "arrow-up"
                            : "arrow-down"
                          : "arrow-up-arrow-down"
                      }
                    />
                  </th>
                  <th style={{ width: "150px" }}>Environment</th>
                  <th style={{ width: "150px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedTab === "DevOps" &&
                paginatedData &&
                paginatedData.length > 0 ? (
                  paginatedData.map((service, index) => (
                    <tr key={index}>
                      <td>{service?.technologyName || "N/A"}</td>
                      <td>{service?.categoryName || "N/A"}</td>
                      <td>{service?.capability || "N/A"}</td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            {Array.isArray(service?.supportingPattern) &&
                            service.supportingPattern.length > 0 ? (
                              service.supportingPattern.map((pattern, i) => (
                                <div
                                  key={pattern.pattern_ref_id}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <SafCheckbox
                                    id={`pattern-${service.tech_radar_id}-${pattern.pattern_ref_id}`}
                                    checked={selectedPatterns.has(
                                      `${service.tech_radar_id}-${pattern.pattern_ref_id}`
                                    )}
                                    onChange={(e) =>
                                      handlePatternCheckboxChange(
                                        service.tech_radar_id,
                                        pattern.pattern_ref_id,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <SafAnchor
                                    href={`https://techtoc.thomsonreuters.com/${pattern.pattern_link}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {pattern.pattern_name}
                                  </SafAnchor>
                                </div>
                              ))
                            ) : (
                              <span>N/A</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{service?.recommendation || "N/A"}</td>
                      <td>NA</td>
                      <td>{service?.provider || "N/A"}</td>
                      <td>{service?.environment || "N/A"}</td>
                      <td>
                        <SafIcon
                          icon-name="trash-can"
                          style={{ cursor: "pointer", color: "#404040" }}
                          onClick={() =>
                            handleDeleteClick(
                              service?.tech_radar_id,
                              service?.environment
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: "center" }}>
                      No DevOps technology stack data available. Click "Add" to
                      add new technology stacks.
                    </td>
                  </tr>
                )}
              </tbody>
            </SafTable>
          </div>
          {filteredData.length > 0 && (
            <div style={{ width: "100%" }}>
              <SafPagination
                data-testid="controlled"
                controlled={true}
                totalItemCount={controlled.totalItemCount}
                currentPageIndex={controlled.currentPageIndex}
                itemsArray={controlled.itemsArray}
                itemsPerPage={controlled.itemsPerPage}
                onPrevious={onPrevious}
                onNext={onNext}
                onChange={onChange}
                onItemsPerPageChange={onItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>
      {/* Delete Confirmation Dialog */}
      <SafDialog
        modal={false}
        is-alert={false}
        is-header={true}
        is-footer={true}
        no-focus-trap={false}
        dialog-title="Do you want to delete this item?"
        hidden={!deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <SafButton appearance="secondary" onClick={confirmDelete}>
            Yes
          </SafButton>
          <SafButton
            appearance="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            No
          </SafButton>
        </div>
      </SafDialog>
      {/* Pattern Removal Confirmation Dialog */}
      <SafDialog
        modal={false}
        is-alert={false}
        is-header={true}
        is-footer={true}
        no-focus-trap={false}
        dialog-title="Remove Selected Patterns"
        hidden={!removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
      >
        <div style={{ marginBottom: "16px" }}>
          Are you sure you want to remove the selected patterns? This action
          cannot be undone.
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <SafButton appearance="secondary" onClick={confirmPatternRemoval}>
            Yes, Remove
          </SafButton>
          <SafButton
            appearance="secondary"
            onClick={() => setRemoveDialogOpen(false)}
          >
            Cancel
          </SafButton>
        </div>
      </SafDialog>
      {/* Alert Component */}
      {openAlertOptions && (
        <SafAlert
          appearance={openAlertOptions[1]}
          alertType={openAlertOptions[0]}
          iconAriaLabel="icon aria label"
          closeAriaLabel="Close"
          onClose={handleOnClose}
        >
          {alertMessage}
        </SafAlert>
      )}
      <JustificationComponent techStack={"devops"} />
      <div style={{ width: "100%", marginTop: "50px" }}>
        <TechnologyStackComparison selectedCloudProvider={selectedCloudProvider} />
      </div>
    </div>
  );
};

export default DevopsTab;
